import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import clsx from 'clsx';
import { AGEING_BUCKETS, formatMoney, PAYMENT_METHODS, type AgeingKey, type CurrencyCode, type PaymentMethod } from '@tengesa/shared';
import { AdjustBalanceDialog, PaymentDialog } from '../customers/CustomersPage';
import { api } from '../../lib/api';
import type { Supplier } from '../../lib/types';
import { useSession } from '../../app/session';
import { Badge, Button, Checkbox, Dialog, EmptyState, ErrorNotice, Field, FormError, PageHeader, Panel, Spinner, Switch, TextArea, TextInput, fieldErrors, formatDateTime, useToast } from '../../ui';

interface SupplierBalance {
  id: string;
  balanceCents: number;
  overdueCents: number;
  dueThisWeekCents: number;
}

export function SuppliersPage() {
  const { me, can } = useSession();
  const cur = me.business.baseCurrency as CurrencyCode;
  const [showAll, setShowAll] = useState(false);
  const [editing, setEditing] = useState<Supplier | 'new' | null>(null);
  const [account, setAccount] = useState<Supplier | null>(null);
  const q = useQuery({ queryKey: ['suppliers', showAll], queryFn: () => api.get<Supplier[]>(`/api/suppliers${showAll ? '?all=true' : ''}`) });
  const seeMoney = can('supplier_payments.manage') || can('reports.financial');
  const balances = useQuery({ queryKey: ['supplier-balances'], queryFn: () => api.get<SupplierBalance[]>('/api/supplier-balances'), enabled: seeMoney });
  const bal = new Map((balances.data ?? []).map((b) => [b.id, b]));
  return (
    <>
      <PageHeader
        title="Suppliers"
        description="Who you buy from, their contacts and payment terms."
        actions={can('suppliers.manage') && <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setEditing('new')}>Add supplier</Button>}
      />
      <Panel>
        <div className="border-b border-line px-5 py-3">
          <Checkbox label="Show deactivated suppliers" checked={showAll} onChange={setShowAll} />
        </div>
        {q.isLoading && <Spinner />}
        {q.error && <div className="p-4"><ErrorNotice error={q.error} onRetry={() => q.refetch()} /></div>}
        {q.data && q.data.length === 0 && <EmptyState title="No suppliers yet">Add the companies you buy stock from.</EmptyState>}
        {q.data && q.data.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-left">
              <thead>
                <tr className="border-b border-line text-sm text-muted">
                  <th className="px-5 py-2.5 font-medium">Supplier</th>
                  <th className="px-3 py-2.5 font-medium">Contact</th>
                  <th className="px-3 py-2.5 font-medium">Terms</th>
                  {seeMoney ? <th className="px-5 py-2.5 text-right font-medium">We owe</th> : <th className="px-5 py-2.5 font-medium">TIN</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {q.data.map((s) => (
                  <tr key={s.id} className="hover:bg-ledger-50/60">
                    <td className="px-5 py-3">
                      {can('suppliers.manage') ? (
                        <button className="font-medium hover:underline" onClick={() => setEditing(s)}>{s.name}</button>
                      ) : (
                        <span className="font-medium">{s.name}</span>
                      )}
                      {!s.isActive && <span className="ml-2"><Badge>Deactivated</Badge></span>}
                    </td>
                    <td className="px-3 py-3 text-sm">
                      {s.contactName ?? '—'}
                      {s.phone && <span className="block text-muted">{s.phone}</span>}
                    </td>
                    <td className="px-3 py-3 text-sm">{s.paymentTermsDays ? `${s.paymentTermsDays} days` : 'Cash on delivery'}</td>
                    {seeMoney ? (
                      <td className="px-5 py-3 text-right">
                        <button className="num hover:underline" onClick={() => setAccount(s)}>{formatMoney(bal.get(s.id)?.balanceCents ?? 0, cur)}</button>
                        {(bal.get(s.id)?.overdueCents ?? 0) > 0 && <span className="block text-xs text-danger num">{formatMoney(bal.get(s.id)!.overdueCents, cur)} overdue</span>}
                        {(bal.get(s.id)?.dueThisWeekCents ?? 0) > 0 && <span className="block text-xs text-warn num">{formatMoney(bal.get(s.id)!.dueThisWeekCents, cur)} due this week</span>}
                      </td>
                    ) : (
                      <td className="px-5 py-3 text-sm num">{s.tin ?? '—'}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      {editing && <SupplierDialog supplier={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
      {account && <SupplierAccountDialog supplier={account} onClose={() => setAccount(null)} />}
    </>
  );
}

interface SupplierStatement {
  balanceCents: number;
  ageing: Record<AgeingKey, number> & { total: number; overdue: number };
  entries: { id: number; kind: string; amountCents: number; dueDate: string | null; refNumber: string | null; method: string | null; reference: string | null; note: string | null; createdAt: string; balanceCents: number }[];
}

const SUPPLIER_KINDS: Record<string, string> = { invoice: 'Delivery invoice', payment: 'Payment', credit_note: 'Credit note', adjustment: 'Correction', opening_balance: 'Balance brought forward' };

function SupplierAccountDialog({ supplier, onClose }: { supplier: Supplier; onClose: () => void }) {
  const { me, can } = useSession();
  const cur = me.business.baseCurrency as CurrencyCode;
  const q = useQuery({ queryKey: ['supplier-statement', supplier.id], queryFn: () => api.get<SupplierStatement>(`/api/suppliers/${supplier.id}/statement`) });
  const [paying, setPaying] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title={`${supplier.name}: what we owe`}
      description={supplier.paymentTermsDays ? `${supplier.paymentTermsDays} days to pay` : 'Paid on delivery'}
      footer={
        can('supplier_payments.manage') && (
          <>
            <Button onClick={() => setAdjusting(true)}>Adjust balance</Button>
            <Button variant="primary" onClick={() => setPaying(true)}>Pay supplier</Button>
          </>
        )
      }
    >
      {q.isLoading && <Spinner />}
      {q.error && <ErrorNotice error={q.error} />}
      {q.data && (
        <>
          <div className="mb-4 grid grid-cols-3 gap-px overflow-hidden rounded-[8px] border border-line bg-line sm:grid-cols-6">
            <div className="col-span-3 bg-surface p-3 sm:col-span-1">
              <p className="text-xs text-muted">Owed</p>
              <p className="font-semibold num">{formatMoney(q.data.balanceCents, cur)}</p>
            </div>
            {AGEING_BUCKETS.map((b) => (
              <div key={b.key} className="bg-surface p-3">
                <p className="text-xs text-muted">{b.label}</p>
                <p className={clsx('font-semibold num', b.key !== 'current' && q.data.ageing[b.key] > 0 && 'text-danger')}>{formatMoney(q.data.ageing[b.key], cur)}</p>
              </div>
            ))}
          </div>
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-line text-muted">
                <th className="py-2 pr-3 font-medium">Date</th>
                <th className="py-2 pr-3 font-medium">Details</th>
                <th className="py-2 pr-3 text-right font-medium">Amount</th>
                <th className="py-2 text-right font-medium">Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {q.data.entries.map((e) => (
                <tr key={e.id}>
                  <td className="py-2 pr-3 text-muted num">{formatDateTime(e.createdAt)}</td>
                  <td className="py-2 pr-3">
                    {SUPPLIER_KINDS[e.kind] ?? e.kind} {e.refNumber}
                    <span className="block text-xs text-muted">
                      {[e.method && PAYMENT_METHODS[e.method as PaymentMethod]?.label.split(' (')[0], e.reference, e.note, e.dueDate && e.amountCents > 0 ? `due ${e.dueDate}` : null].filter(Boolean).join(' — ')}
                    </span>
                  </td>
                  <td className="py-2 pr-3 text-right num">{formatMoney(e.amountCents, cur)}</td>
                  <td className="py-2 text-right font-medium num">{formatMoney(e.balanceCents, cur)}</td>
                </tr>
              ))}
              {q.data.entries.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-4 text-muted">Nothing recorded yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </>
      )}
      {paying && <PaymentDialog kind="supplier" id={supplier.id} name={supplier.name} onClose={() => setPaying(false)} />}
      {adjusting && <AdjustBalanceDialog kind="supplier" id={supplier.id} name={supplier.name} onClose={() => setAdjusting(false)} />}
    </Dialog>
  );
}

export function SupplierDialog({ supplier, onClose, onSaved }: { supplier: Supplier | null; onClose: () => void; onSaved?: (s: Supplier) => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [v, setV] = useState({
    name: supplier?.name ?? '',
    contactName: supplier?.contactName ?? '',
    phone: supplier?.phone ?? '',
    email: supplier?.email ?? '',
    address: supplier?.address ?? '',
    tin: supplier?.tin ?? '',
    vatNumber: supplier?.vatNumber ?? '',
    paymentTermsDays: supplier?.paymentTermsDays ?? 0,
    notes: supplier?.notes ?? '',
    isActive: supplier?.isActive ?? true,
  });
  const set = <K extends keyof typeof v>(k: K, val: (typeof v)[K]) => setV((p) => ({ ...p, [k]: val }));
  const m = useMutation({
    mutationFn: () => (supplier ? api.put<Supplier>(`/api/suppliers/${supplier.id}`, v) : api.post<Supplier>('/api/suppliers', v)),
    onSuccess: (s) => {
      toast(supplier ? 'Supplier saved' : 'Supplier added');
      void qc.invalidateQueries({ queryKey: ['suppliers'] });
      onSaved?.(s);
      onClose();
    },
  });
  const f = fieldErrors(m.error);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    m.mutate();
  };
  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title={supplier ? supplier.name : 'Add supplier'}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="supplier-form" busy={m.isPending}>{supplier ? 'Save supplier' : 'Add supplier'}</Button>
        </>
      }
    >
      <FormError error={m.error} />
      <form id="supplier-form" onSubmit={submit} className="grid gap-4 sm:grid-cols-2" noValidate>
        <Field label="Company name" htmlFor="s-name" error={f.name}>
          <TextInput id="s-name" value={v.name} onChange={(e) => set('name', e.target.value)} invalid={!!f.name} />
        </Field>
        <Field label="Contact person" htmlFor="s-contact" error={f.contactName}>
          <TextInput id="s-contact" value={v.contactName} onChange={(e) => set('contactName', e.target.value)} />
        </Field>
        <Field label="Phone" htmlFor="s-phone" error={f.phone}>
          <TextInput id="s-phone" type="tel" value={v.phone} onChange={(e) => set('phone', e.target.value)} />
        </Field>
        <Field label="Email" htmlFor="s-email" error={f.email}>
          <TextInput id="s-email" type="email" value={v.email} onChange={(e) => set('email', e.target.value)} invalid={!!f.email} />
        </Field>
        <Field label="Address" htmlFor="s-address" className="sm:col-span-2">
          <TextInput id="s-address" value={v.address} onChange={(e) => set('address', e.target.value)} />
        </Field>
        <Field label="ZIMRA TIN" htmlFor="s-tin" error={f.tin}>
          <TextInput id="s-tin" inputMode="numeric" maxLength={10} value={v.tin} onChange={(e) => set('tin', e.target.value.replace(/\D/g, ''))} invalid={!!f.tin} />
        </Field>
        <Field label="VAT number" htmlFor="s-vat" error={f.vatNumber}>
          <TextInput id="s-vat" inputMode="numeric" maxLength={9} value={v.vatNumber} onChange={(e) => set('vatNumber', e.target.value.replace(/\D/g, ''))} invalid={!!f.vatNumber} />
        </Field>
        <Field label="Payment terms (days)" htmlFor="s-terms" error={f.paymentTermsDays} hint="0 means you pay on delivery">
          <TextInput id="s-terms" inputMode="numeric" value={String(v.paymentTermsDays)} onChange={(e) => set('paymentTermsDays', Number(e.target.value.replace(/\D/g, '') || 0))} />
        </Field>
        <Field label="Notes" htmlFor="s-notes" className="sm:col-span-2">
          <TextArea id="s-notes" rows={2} value={v.notes} onChange={(e) => set('notes', e.target.value)} />
        </Field>
        {supplier && (
          <div className="flex items-center justify-between gap-4 rounded-[8px] bg-paper px-4 py-3 sm:col-span-2">
            <span className="font-medium">Active supplier</span>
            <Switch checked={v.isActive} onChange={(x) => set('isActive', x)} label="Active supplier" />
          </div>
        )}
      </form>
    </Dialog>
  );
}
